<template>
  <div class="matrixx-grid">
    <table class="matrixx-table">
      <thead>
        <tr>
          <th></th>
          <th v-for="(answer, index) in columns" :key="answer.id">
            <div class="d-flex justify-content-center align-items-center">
              <div v-if="editing && columns.length > 1" class="delete-button mr-2">
                <button @click="deleteColumn(answer)" class="btn btn-link p-0">
                  <TrashIcon />
                </button>
              </div>
              <EditableField
                :ref="`editable_field:the_answer:${index}`"
                v-model="answer.the_answer"
                @editable_field:save="saveColumn(answer)"
                placeholder="Column title"
                :editable="editing"
              />
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr class="matrixx-row" v-for="(row, index) in rows" :key="row.id">
          <td class="matrixx-question-cell d-flex align-items-center">
            <div v-if="editing && rows.length > 1" class="delete-button mr-2">
              <button @click="deleteRow(row)" class="btn btn-link p-0">
                <TrashIcon />
              </button>
            </div>
            <EditableField
              :ref="`editable_field:the_question:${index}`"
              v-model="row.the_question"
              @editable_field:save="saveRow(row)"
              placeholder="Row title"
              :editable="editing"
            />
          </td>
          <td class="matrixx-radio-cell" v-for="answer in row.answers" :key="answer.value">
            <label class="clickable-area" :id="`answer-${answer.id}`">
              <b-form-radio
                type="radio"
                :name="`answer-${answer.id}`"
                :value="answer.id"
                v-model="responses[row.id]"
                @change="answerQuestion(row.id, answer.id)"
              >
                <span class="responsive-label">{{ answer.the_answer }}</span>
              </b-form-radio>
            </label>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="editing && !addingSomething" class="d-flex ml-3 mt-3">
      <button class="btn p-0" @click="addRow" data-cy="add-matrix-row">
        <div class="d-flex align-items-center">
          <PlusIcon />
          <span class="ml-2">Add row</span>
        </div>
      </button>
      <button
        class="btn p-0 ml-3"
        @click="addColumn"
        v-if="columns.length < 6"
        data-cy="add-matrix-column"
      >
        <div class="d-flex align-items-center">
          <PlusIcon />
          <span class="ml-2">Add column</span>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useSurveyResponseStore } from '@app/stores/SurveyResponseStore.ts'
import EditableField from '@app/components/EditableField.vue'
import PlusIcon from '@app/graphics/PlusIcon.vue'
import TrashIcon from '@app/graphics/TrashIcon.vue'

export default {
  components: { EditableField, PlusIcon, TrashIcon },
  props: {
    question: {
      type: Object,
      required: true,
    },
    recipientId: {
      type: String,
      default: null,
    },
    // Differently from other components that only have a `preview: boolean` prop,
    // MatrixxGrid has a `mode: string` prop that can be 'preview' or 'edit' - or the default 'live',
    // which, in fact, could be any other string.
    // The reason for this is that this component is shared between app/components/Matrixx
    // which is used in the editor (mode: edit) and survey/components/MatrixxQuestion, which is used in the survey (mode: preview/live).
    mode: {
      type: String,
      default: 'live',
    },
  },
  data() {
    return {
      // responses: { <question_id>: <answer_id> }
      responses: {},
      transientQuestions: [],
      transientAnswers: [],
      editable: 'Add new column',
    }
  },
  created() {
    // Initialize local `responses` with existing survey responses
    this.existingResponses.forEach((response) => {
      this.responses[response.question_id] = response.answer_id.toString()
    })
  },
  computed: {
    ...mapState(useSurveyResponseStore, ['surveyResponses', 'upsertSurveyResponse']),
    previewing() {
      return this.mode === 'preview'
    },
    editing() {
      return this.mode === 'edit'
    },
    addingSomething() {
      return this.transientQuestions.length > 0 || this.transientAnswers.length > 0
    },
    existingResponses() {
      const matrixRowsIds = this.question.rows.map((row) => row.id)
      return this.surveyResponses.filter((response) => {
        // when a recipient_id is present we check for that too
        // this is set usually when we are in the customer spotlight (Results page)
        if (this.recipientId) {
          return (
            matrixRowsIds.includes(response.question_id) &&
            Number(this.recipientId) === response.recipient_id
          )
        } else {
          return matrixRowsIds.includes(response.question_id)
        }
      })
    },
    answers() {
      // This `answers` is only used for rendering the table header.
      // The actual answers for matrixes are stored in `this.question.rows[].answers`.
      return this.question.answers
    },
    questions() {
      return this.question.rows
    },
    columns() {
      return [...this.answers, ...this.transientAnswers]
    },
    rows() {
      return [...this.questions, ...this.transientQuestions]
    },
  },
  methods: {
    answerQuestion(questionId, answerId) {
      if (this.previewing) return

      this.$emit('matrixx-value-changed', { questionId, answerId })
    },
    addRow() {
      this.transientQuestions.push({
        the_question: '',
      })
      this.$nextTick(() =>
        this.$refs[`editable_field:the_question:${this.rows.length - 1}`][0].enterEdit(),
      )
    },
    saveRow(row) {
      // Tell somebody to save it to DB
      if (!row.id) this.deleteRow(row)
      this.$emit('on-save-row', row)
    },
    deleteRow(row) {
      if (row.id) {
        // Tell somebody to delete it from DB
        this.$emit('on-delete-row', row.id)
      } else {
        // Just delete the transient row
        const index = this.transientQuestions.indexOf(row)
        this.transientQuestions.splice(index, 1)
      }
    },
    addColumn() {
      this.transientAnswers.push({
        the_answer: '',
      })
      this.$nextTick(() =>
        this.$refs[`editable_field:the_answer:${this.columns.length - 1}`][0].enterEdit(),
      )
    },
    saveColumn(answer) {
      // Tell somebody to save it to DB
      if (!answer.id) this.deleteColumn(answer)
      this.$emit('on-save-column', answer)
    },
    deleteColumn(answer) {
      if (answer.id) {
        // Tell somebody to delete it from DB
        this.$emit('on-delete-column', answer.id)
      } else {
        // Just delete the transient column
        const index = this.transientAnswers.indexOf(answer)
        this.transientAnswers.splice(index, 1)
      }
    },
  },
}
</script>

<style scoped lang="sass">
.matrixx-table
  width: 100%
  border-collapse: collapse

  thead
    th
      text-align: center
      font-weight: 500
      width: 10000px
      min-width: 4em

  tr
    border-bottom: 1px solid #ddd
  tr:last-child
    border-bottom: none

  td
    padding: 10px
    text-align: center

  tr td:first-child
    text-align: left
    max-width: 300px
    word-wrap: break-word

  .matrixx-row
    .responsive-label
      display: none
    .matrixx-question-cell
      font-weight: 500
      padding: 10px 0
    .matrixx-radio-cell
      padding: 5px
      .clickable-area
        position: relative
        display: block

@media (max-width: 560px)
  thead
    th
      display: none

  .matrixx-row
    display: flex
    flex-direction: column
    .matrixx-radio-cell
      text-align: left
      .responsive-label
        display: block
</style>
